import { ErrorHandler } from '@src/components/ErrorHandler';
import { ListCountries, ListTerritoryProvinces } from '@src/includes/functions';
import { fieldValidator } from '@src/includes/validation';
import { isRequired } from '@src/routes/private/MemberProfile/components/Modals/includes/functions';
import { ToolTipStyles } from '@src/routes/private/MemberProfile/components/Modals/includes/styles';
import { Box } from '@uhg-abyss/web/ui/Box';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { Text } from '@uhg-abyss/web/ui/Text';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';

import fields from '../../../../includes/fields.json';

/**
 * Tab: InternationalAddress
 *
 * Displays international address information about a member's policy.
 *
 * @param props
 * @returns {Element}
 * @constructor
 */
export function InternationalAddress(props) {
  const { form, isInternational, isLineValid, targetRef, voidFormDisabled } = props;

  const agencyCaseId = form.watch('ui[internationalAddress][agencyCaseId]');
  const agentOfRecord = form.watch('ui[internationalAddress][agentOfRecord]');
  const agentPhoneNumber = form.watch('ui[internationalAddress][agentPhoneNumber]');
  const agentPhoneType = form.watch('ui[internationalAddress][agentPhoneType]');
  const city = form.watch('ui[internationalAddress][city]');
  const countryCode = form.watch('ui[internationalAddress][countryCode]');
  const guardianFirstName = form.watch('ui[internationalAddress][guardianFirstName]');
  const guardianLastName = form.watch('ui[internationalAddress][guardianLastName]');
  const guardianPhoneNumber = form.watch('ui[internationalAddress][guardianPhoneNumber]');
  const guardianPhoneType = form.watch('ui[internationalAddress][guardianPhoneType]');
  const postalCode = form.watch('ui[internationalAddress][postalCode]');
  const stateOrRegion = form.watch('ui[internationalAddress][stateOrRegion]');
  const streetAddress = form.watch('ui[internationalAddress][streetAddress]');
  const suite = form.watch('ui[internationalAddress][suite]');

  /**
   * ListCountries
   *
   * Retrieves a list of countries.
   *
   * @type {{code: *, name: *}[]}
   */
  const Countries = useMemo(() => {
    return ListCountries().filter((country) => {
      return !(isInternational && country?.code === 'US');
    });
  }, [isInternational]);

  /**
   * TerritoriesProvinces
   *
   * Retrieves a list of states.
   *
   * @type {{code: *, name: *}[]}
   */
  const TerritoriesProvinces = useMemo(() => {
    return ListTerritoryProvinces(countryCode);
  }, [countryCode]);

  useEffect(() => {
    if (!isEmpty(agencyCaseId)) {
      form.validate(
        'ui[internationalAddress][agencyCaseId]',
        () => { },
        () => { }
      );
    }

    if (!isEmpty(agentOfRecord)) {
      form.validate(
        'ui[internationalAddress][agentOfRecord]',
        () => { },
        () => { }
      );
    }

    if (!isEmpty(guardianFirstName)) {
      form.validate(
        'ui[internationalAddress][guardianFirstName]',
        () => { },
        () => { }
      );
    }

    if (!isEmpty(guardianLastName)) {
      form.validate(
        'ui[internationalAddress][guardianLastName]',
        () => { },
        () => { }
      );
    }

    if (!isEmpty(streetAddress)) {
      form.validate(
        'ui[internationalAddress][streetAddress]',
        () => { },
        () => { }
      );
    }

    if (!isEmpty(suite)) {
      form.validate(
        'ui[internationalAddress][suite]',
        () => { },
        () => { }
      );
    }

    if (!isEmpty(postalCode)) {
      form.validate(
        'ui[internationalAddress][postalCode]',
        () => { },
        () => { }
      );
    }
  }, [agentOfRecord, agencyCaseId, guardianFirstName, guardianLastName, streetAddress, suite, postalCode]);

  return (
    <ErrorHandler location="src/routes/private/MemberProfile/components/Modals/Qmcso/components/Address/components/InternationalAddress/InternationalAddress.jsx">
      <Grid className="section first">
        <Box className="box">
          <Heading offset={5}>
            Address Line 1: <span className="required-field" />
          </Heading>
        </Box>
        <span ref={targetRef} />
        <Grid.Col span={{ xs: 12 }}>
          <Text color={isLineValid ? '' : '$error1'} size="$sm">
            <em>Please enter either Agent of Record or Guardian information.</em>
          </Text>
        </Grid.Col>
        <Grid.Col span={{ lg: 7, xl: 7, xs: 12 }}>
          <Grid>
            <Grid.Col span={{ lg: 6, xl: 6, xs: 12 }}>
              <TextInput
                {...fields?.internationalAddress?.agentOfRecord}
                isDisabled={voidFormDisabled}
                label={
                  <ToolTipStyles>
                    <Tooltip asChild content="Mail will be addressed with ATTN to this Agent of Record">
                      <strong>{fields?.internationalAddress?.agentOfRecord?.label}</strong>
                    </Tooltip>
                  </ToolTipStyles>
                }
                validators={{
                  validate: {
                    customValidator: (value) => {
                      return fieldValidator(fields?.internationalAddress?.agentOfRecord, value);
                    },
                  },
                }}
              />
            </Grid.Col>
            <Grid.Col span={{ lg: 6, xl: 6, xs: 12 }}>
              <TextInput
                {...fields?.internationalAddress?.agencyCaseId}
                formatter={(value) => {
                  return value.replace(/[-`&/\\#,+()$~%.'":*?<>{}@!^_=;\][|]/g, '');
                }}
                isDisabled={voidFormDisabled}
                label={
                  <ToolTipStyles>
                    <Tooltip
                      asChild
                      content="The case Identifier is required when communication is being sent to the Agent of Record"
                    >
                      <strong>{fields?.internationalAddress?.agencyCaseId?.label}</strong>
                    </Tooltip>
                  </ToolTipStyles>
                }
                validators={{
                  validate: {
                    customValidator: (value) => {
                      return fieldValidator(fields?.internationalAddress?.agencyCaseId, value);
                    },
                  },
                }}
              />
            </Grid.Col>
          </Grid>
        </Grid.Col>
        <Grid.Col span={{ lg: 5, xl: 5, xs: 12 }}>
          <Grid>
            <Grid.Col span="45%">
              <SelectInput
                {...fields?.internationalAddress?.agentPhoneType}
                isDisabled={voidFormDisabled}
                label={
                  <ToolTipStyles>
                    <Tooltip asChild content="Indicates what type of phone number is being entered for communication">
                      <strong>{fields?.internationalAddress?.agentPhoneType?.label}</strong>
                    </Tooltip>
                  </ToolTipStyles>
                }
                validators={isRequired(agentPhoneNumber)}
              />
            </Grid.Col>
            <Grid.Col span="55%">
              <TextInput
                {...fields?.internationalAddress?.agentPhoneNumber}
                isDisabled={voidFormDisabled || isEmpty(agentPhoneType)}
              />
            </Grid.Col>
          </Grid>
        </Grid.Col>
        <Grid.Col span={{ lg: 7, xl: 7, xs: 12 }}>
          <Grid>
            <Grid.Col span={{ lg: 6, xl: 6, xs: 12 }}>
              <TextInput
                {...fields?.internationalAddress?.guardianFirstName}
                isDisabled={voidFormDisabled}
                label={
                  <ToolTipStyles>
                    <Tooltip
                      asChild
                      content="Mail will be addressed with ATTN to this person only if there is no Agent of Record indicated. (Agent of record will take precedence)"
                    >
                      <strong>{fields?.internationalAddress?.guardianFirstName?.label}</strong>
                    </Tooltip>
                  </ToolTipStyles>
                }
                validators={{
                  ...isRequired(guardianLastName),
                  ...{
                    validate: {
                      customValidator: (value) => {
                        return fieldValidator(fields?.internationalAddress?.guardianFirstName, value);
                      },
                    },
                  },
                }}
              />
            </Grid.Col>
            <Grid.Col span={{ lg: 6, xl: 6, xs: 12 }}>
              <TextInput
                {...fields?.internationalAddress?.guardianLastName}
                isDisabled={voidFormDisabled}
                validators={{
                  ...isRequired(guardianFirstName),
                  ...{
                    validate: {
                      customValidator: (value) => {
                        return fieldValidator(fields?.internationalAddress?.guardianLastName, value);
                      },
                    },
                  },
                }}
              />
            </Grid.Col>
          </Grid>
        </Grid.Col>
        <Grid.Col span={{ lg: 5, xl: 5, xs: 12 }}>
          <Grid>
            <Grid.Col span="45%">
              <SelectInput
                {...fields?.internationalAddress?.guardianPhoneType}
                isDisabled={voidFormDisabled}
                label={
                  <ToolTipStyles>
                    <Tooltip asChild content="Indicates what type of phone number is being entered for communication">
                      <strong>{fields?.internationalAddress?.guardianPhoneType?.label}</strong>
                    </Tooltip>
                  </ToolTipStyles>
                }
                validators={isRequired(guardianPhoneNumber)}
              />
            </Grid.Col>
            <Grid.Col span="55%">
              <TextInput
                {...fields?.internationalAddress?.guardianPhoneNumber}
                isDisabled={voidFormDisabled || isEmpty(guardianPhoneType)}
              />
            </Grid.Col>
          </Grid>
        </Grid.Col>
      </Grid>
      <Grid className="section">
        <Box className="box">
          <Heading offset={5}>Address Line 2:</Heading>
        </Box>
        <Grid.Col span={{ lg: 4, xl: 4, xs: 12 }}>
          <TextInput
            {...fields?.internationalAddress?.streetAddress}
            isDisabled={voidFormDisabled}
            label={
              <ToolTipStyles>
                <Tooltip
                  asChild
                  content="If address is unknown, enter QMCSO default address until correct address is known"
                >
                  <strong>{fields?.internationalAddress?.streetAddress?.label}</strong>
                </Tooltip>
              </ToolTipStyles>
            }
            validators={{
              ...fields?.internationalAddress?.streetAddress?.validators,
              validate: {
                customValidator: (value) => {
                  return fieldValidator(fields?.internationalAddress?.streetAddress, value);
                },
              },
            }}
          />
        </Grid.Col>
      </Grid>
      <Grid className="section">
        <Box className="box">
          <Heading offset={5}>Address Line 3:</Heading>
        </Box>
        <Grid.Col span={{ lg: 4, xl: 4, xs: 12 }}>
          <TextInput
            {...fields?.internationalAddress?.suite}
            isDisabled={voidFormDisabled}
            validators={{
              ...fields?.internationalAddress?.suite?.validators,
              validate: {
                customValidator: (value) => {
                  return fieldValidator(fields?.internationalAddress?.suite, value);
                },
              },
            }}
          />
        </Grid.Col>
      </Grid>
      <Grid className="section">
        <Box className="box">
          <Heading offset={5}>Address Line 4:</Heading>
        </Box>
        <Grid.Col span={{ lg: 3, xl: 3, xs: 12 }}>
          <SelectInput
            {...fields?.internationalAddress?.countryCode}
            options={Countries.map((country) => {
              return {
                label: country?.name,
                value: country?.code,
              };
            })}
          />
        </Grid.Col>
        <Grid.Col span={{ lg: 3, xl: 3, xs: 12 }}>
          <SelectInput
            {...fields?.internationalAddress?.stateOrRegion}
            isDisabled={voidFormDisabled || isEmpty(countryCode)}
            options={TerritoriesProvinces.map((territoryProvince) => {
              return {
                label: territoryProvince?.name,
                value: territoryProvince?.code,
              };
            })}
          />
        </Grid.Col>
        <Grid.Col span={{ lg: 3, xl: 3, xs: 12 }}>
          <TextInput
            {...fields?.internationalAddress?.city}
            isDisabled={voidFormDisabled || isEmpty(countryCode) || isEmpty(stateOrRegion)}
          />
        </Grid.Col>
        <Grid.Col span={{ lg: 3, xl: 3, xs: 12 }}>
          <TextInput
            {...fields?.internationalAddress?.postalCode}
            formatter={(value) => {
              return value.replace(/[-`&/\\#,+()$~%.'":*?<>{}@!^_=;\][|]/g, '');
            }}
            isDisabled={voidFormDisabled || isEmpty(countryCode) || isEmpty(stateOrRegion) || isEmpty(city)}
            label={
              <ToolTipStyles>
                <Tooltip asChild content="will allow zip + 4 or international zip code">
                  <strong>{fields?.internationalAddress?.postalCode?.label}</strong>
                </Tooltip>
              </ToolTipStyles>
            }
            validators={{
              ...fields?.internationalAddress?.postalCode?.validators,
              validate: {
                customValidator: (value) => {
                  return fieldValidator(fields?.internationalAddress?.postalCode, value);
                },
              },
            }}
          />
        </Grid.Col>
      </Grid>
    </ErrorHandler>
  );
}

InternationalAddress.propTypes = {
  form: PropTypes.shape({
    getValues: PropTypes.func,
    setValue: PropTypes.func,
  }),
  isAddressValid: PropTypes.bool,
  targetRef: PropTypes.shape({
    current: PropTypes.shape({
      scrollIntoView: PropTypes.func,
    }),
  }),
  TerritoriesProvinces: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string,
    })
  ),
  voidFormDisabled: PropTypes.bool,
};

InternationalAddress.defaultProps = {
  form: {},
  isAddressValid: false,
  targetRef: {},
  TerritoriesProvinces: [],
  voidFormDisabled: false,
};
